import React, { useEffect, useState } from "react"
import { getDashboardSummaryByDateRange } from "../../../services/dashboard.service"
import Card from "../../../components/atoms/Card.atom"
import { Skeleton } from "../../../components/atoms/misc/Skeleton.atom"
import {
  displayValueOrPlaceholder,
  formatPercentageSuffix,
} from "../../../utils/strings.utils"
import { ReactComponent as PercentageViewIcon } from "../../../design/assests/images/icons/percentage-view.svg"
import UserActiveIcon from "../../../design/assests/images/icons/user-active.js"
import useReportFiltersContext from "../../../hooks/contextConsumers/useReportFiltersContext.hook.js"
import IconWithTooltipAndCustomBody from "../../../components/molecules/IconWithTooltipAndCustomBody.molecule"

const INITAL_DASHBOARD_SUMMARY_STATE = {
  totalSisuTime: 0,
  totalActiveProjects: 0,
  totalActiveTeamMembers: 0,
  teamOccupationPercentage: 0,
}

const DashboardSummary = () => {
  const { dashboardDateRange: dateRange } = useReportFiltersContext()
  const [summaryData, setSummaryData] = useState(INITAL_DASHBOARD_SUMMARY_STATE)
  const [loadingSummary, setLoadingSummary] = useState(false)

  useEffect(() => {
    getSummaryData()
  }, [dateRange])

  const getSummaryData = async () => {
    setLoadingSummary(true)
    try {
      const response = await getDashboardSummaryByDateRange(
        dateRange?.[0],
        dateRange?.[1]
      )
      if (response.success) {
        setSummaryData(response.result)
      } else {
        throw new Error(response.message)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setLoadingSummary(false)
    }
  }

  const CardTemplate = ({
    icon,
    iconClassName,
    title,
    data,
    className,
    loading = false,
  }) => {
    const DEFAULT_SHADOW = "shadow-2"
    return (
      <Card
        className={`dashboard-card card--with-title-icon ${DEFAULT_SHADOW} ${className}`}
      >
        <div className="flex gap-2 align-items-center title">
          {iconClassName ?
            <i
              className={`${iconClassName}`}
              style={{
                color:
                  "light-dark(var(--primary-color-highlight), var(--surface-highlight__light))",
              }}
            />
          : icon}
          <span
            style={{ fontSize: "min(3.25vw, 18px)", lineHeight: "1" }}
            className="font-semibold"
          >
            {title}
          </span>
        </div>
        <span className="text-4xl font-bold flex align-items-bottom">
          {loading ?
            <Skeleton height={39} />
          : data}
        </span>
      </Card>
    )
  }

  return (
    <div className="flex gap-3 flex-wrap mb-3 card-stats-container">
      <CardTemplate
        title="Horas trabajadas"
        data={displayValueOrPlaceholder(summaryData.totalSisuTime)}
        iconClassName="pi pi-clock text-3xl"
        className="flex-1 font-cairo flex-shrink-0"
        loading={loadingSummary}
      />
      <CardTemplate
        title={
          <div className="flex align-items-center gap-2">
            <span>Ocupación</span>
            <IconWithTooltipAndCustomBody
              tooltipBody="Horas trabajadas para cliente con respecto a las horas trabajadas en total"
              position="right"
              iconClassName="pi pi-info-circle mx-1"
            />
          </div>
        }
        data={displayValueOrPlaceholder(
          summaryData.teamOccupationPercentage,
          formatPercentageSuffix
        )}
        icon={<PercentageViewIcon />}
        className="flex-1 font-cairo flex-shrink-0"
        loading={loadingSummary}
      />
      <CardTemplate
        title="Proyectos activos"
        data={displayValueOrPlaceholder(summaryData.totalActiveProjects)}
        iconClassName="pi pi-folder-open text-3xl"
        className="flex-1 font-cairo flex-shrink-0"
        loading={loadingSummary}
      />
      <CardTemplate
        title="Integrantes con actividades"
        data={displayValueOrPlaceholder(summaryData.totalActiveTeamMembers)}
        icon={<UserActiveIcon className="flex-shrink-0" />}
        className="flex-1 font-cairo flex-shrink-0"
        loading={loadingSummary}
      />
    </div>
  )
}

export default DashboardSummary
